// chart.jsx - The price chart, as React. All of the arithmetic is in
// lib/chart.js, which the standalone embed uses too; this file is only the SVG
// and the pointer handling.
//
// Two things it must never do, both of which are why it measures rather than
// using a viewBox. A viewBox with preserveAspectRatio="none" stretches the
// stroke into an oval the moment a container is not the shape it was drawn for,
// and every embed is a container we did not choose. And a chart that overflows
// its box puts a scrollbar on somebody else's website, which is the fastest way
// to have an embed removed.
//
// Gradient ids come from useId(). Two charts on one page would otherwise share
// one id and the second would take the first one's fill.
//
// LEGEND
//   1. useMeasure()   the element's real pixel size, kept current
//   2. PriceChart     the full chart: axis, fill, crosshair
//   3. Sparkline      the same line at two centimetres, no furniture
//   4. Change         a signed percentage, coloured

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { plot, crosshair, crosshairLabel, usd, signed, UP, DOWN } from "../lib/chart.js";

////////// 1. MEASURE //////////
// Nothing renders until this has a width, so every caller has to give the
// wrapper a height of its own. That is deliberate: a chart that sizes itself
// from its content is a chart that changes the page layout when data arrives.

export function useMeasure() {
  const ref = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Round, or a fractional resize loop can re-render on every frame.
      setBox((previous) => {
        const next = { width: Math.round(width), height: Math.round(height) };
        return next.width === previous.width && next.height === previous.height ? previous : next;
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, box];
}

////////// 2. PRICE CHART //////////

export function PriceChart({
  points = [],
  range = "1d",
  color = "#8a6534",
  height = 300,
  axis = true,
  live = true,
  className,
  label = "Price history",
}) {
  const [ref, box] = useMeasure();
  const gradientId = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);

  const geometry = useMemo(
    () => plot(points, { width: box.width, height: box.height || height, axis, range }),
    [points, box.width, box.height, height, axis, range]
  );

  const onMove = useCallback((event) => {
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    setHover(crosshair(points, geometry, event.clientX - rect.left));
  }, [points, geometry]);

  const clear = useCallback(() => setHover(null), []);

  const rising = geometry.last && geometry.first != null && geometry.last.value >= geometry.first;

  return (
    <div ref={ref} className={clsx("relative w-full", className)} style={{ height }}>
      {geometry.empty ? (
        <div className="absolute inset-0 grid place-items-center">
          <p className="hint">Not enough history to draw this range yet.</p>
        </div>
      ) : (
        <>
          <svg
            width={geometry.width}
            height={geometry.height}
            className="block select-none"
            role="img"
            aria-label={label}
            // pan-y keeps a phone scrolling vertically while a finger dragged
            // sideways reads the line.
            style={{ touchAction: "pan-y" }}
            onPointerMove={onMove}
            onPointerDown={onMove}
            onPointerLeave={clear}
            onPointerCancel={clear}
          >
            <defs>
              <linearGradient id={`fill-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Horizontal guides, and the value each one stands for. */}
            {axis && geometry.gridY.map((guide) => (
              <g key={guide.value}>
                <line
                  x1={geometry.pad.left}
                  x2={geometry.pad.left + geometry.plotWidth}
                  y1={guide.y}
                  y2={guide.y}
                  stroke="currentColor"
                  strokeOpacity="0.11"
                  strokeWidth="1"
                />
                <text
                  x={geometry.pad.left + geometry.plotWidth + 8}
                  y={guide.y + 3.5}
                  fontSize="11"
                  fill="currentColor"
                  fillOpacity="0.5"
                  className="num"
                >
                  {guide.label}
                </text>
              </g>
            ))}

            <path d={geometry.area} fill={`url(#fill-${gradientId})`} />
            <path
              d={geometry.line}
              fill="none"
              stroke={color}
              strokeWidth="1.75"
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {axis && geometry.ticks.map((tick) => (
              <text
                key={tick.ms}
                x={tick.x}
                y={geometry.height - 6}
                fontSize="11"
                fill="currentColor"
                fillOpacity="0.45"
                textAnchor={tick.x < 24 ? "start" : "middle"}
              >
                {tick.label}
              </text>
            ))}

            {/* The last reading, with a slow ring so a still image still reads
                as something that is running. */}
            {live && geometry.last && (
              <>
                <circle cx={geometry.last.x} cy={geometry.last.y} r="7" fill={color} opacity="0.16" className="chart-pulse" />
                <circle cx={geometry.last.x} cy={geometry.last.y} r="3" fill={color} />
              </>
            )}

            {hover && (
              <g>
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={geometry.pad.top}
                  y2={geometry.pad.top + geometry.plotHeight}
                  stroke="currentColor"
                  strokeOpacity="0.28"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                <circle cx={hover.x} cy={hover.y} r="4" fill={color} stroke="var(--color-card, #fff)" strokeWidth="1.5" />
              </g>
            )}
          </svg>

          {/* The read-out sits outside the SVG so it can use real type. Clamped
              to the box on both sides, because near the right hand edge an
              unclamped tooltip is what puts a scrollbar on the page. */}
          {hover && (
            <div
              className="chart-readout"
              style={{ left: Math.min(Math.max(hover.x, 54), Math.max(geometry.width - 54, 54)) }}
            >
              <span className="num font-semibold">{usd(hover.value)}</span>
              <span className="chart-readout-time">{crosshairLabel(hover.ms, range)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

////////// 3. SPARKLINE //////////
// No axis, no labels, no interaction. A shape, at the size of a word.

export function Sparkline({ points = [], color = "#8a6534", width = 88, height = 26, className }) {
  const geometry = useMemo(
    () => plot(points, { width, height, axis: false, padTop: 3, padBottom: 3, gridCount: 2 }),
    [points, width, height]
  );

  if (geometry.empty) return <span className={clsx("inline-block", className)} style={{ width, height }} />;

  return (
    <svg width={width} height={height} className={clsx("block", className)} aria-hidden="true">
      <path d={geometry.line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

////////// 4. CHANGE //////////

export function Change({ percent, amount, className, showAmount = false }) {
  if (!Number.isFinite(percent)) return null;
  const flat = Math.abs(percent) < 0.005;
  const color = flat ? "currentColor" : percent > 0 ? UP : DOWN;

  return (
    <span className={clsx("num inline-flex items-baseline gap-1.5 font-semibold", className)} style={{ color }}>
      <span>{flat ? "0.00%" : signed(percent)}</span>
      {showAmount && Number.isFinite(amount) && (
        <span className="font-normal opacity-70">{signed(amount, "")}</span>
      )}
    </span>
  );
}
